import { Data, Menu, Utils } from 'common';
import isNumber from 'lodash/isNumber';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { ActiveMenuItemId } from './shared/ActiveMenuItemId.enum';
import DateRangeOptions from './shared/DateRangeOptions';
import EligibleForOptions, { EligibleFor } from './shared/EligibleForOptions';
import StatusOptions, { PrintStatus } from './shared/StatusOptions';

const { Carrier, Capability, InternalCapability } = Data;
const { ActionType, ItemType, MenuPanel } = Menu;
const { Session, Util } = Utils;

const LeftMenu = ({
	activeMenuItem = {},
	totalCount,
	getDefaultActiveMenuItem,
	onMenuItemClick,
	onAdvancedSearchTrigger,
	users,
}) => {
	const { activeMenuItemId, activeMenuItemValue } = activeMenuItem;
	const { globalpost = {} } = Session.getAccountInfo();
	const { firstMileCarrier = '' } = globalpost;
	const menuItems = [
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Search,
			title: 'Search Prints',
			active: activeMenuItemId === ActiveMenuItemId.Search,
			showBadge: isNumber(totalCount),
			badge: totalCount,
			data: {
				activeMenuItemId: ActiveMenuItemId.Search,
				leftMenuTitle: 'Search Results',
			},
			dataqa: 'search-prints',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Date Printed',
			active: activeMenuItemId === ActiveMenuItemId.DatePrinted,
			collapsed: activeMenuItemId !== ActiveMenuItemId.DatePrinted,
			items: DateRangeOptions.map(({ label, value, dataqa }) => ({
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: label,
				active:
					activeMenuItemId === ActiveMenuItemId.DatePrinted &&
					activeMenuItemValue === value,
				showBadge:
					activeMenuItemId === ActiveMenuItemId.DatePrinted &&
					activeMenuItemValue === value &&
					isNumber(totalCount),
				badge: totalCount,
				data: {
					activeMenuItemId: ActiveMenuItemId.DatePrinted,
					activeMenuItemValue: value,
					leftMenuTitle: label,
				},
				dataqa,
			})),
			dataqa: 'date-printed',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Eligible For',
			active: activeMenuItemId === ActiveMenuItemId.EligibleFor,
			collapsed: activeMenuItemId !== ActiveMenuItemId.EligibleFor,
			items: EligibleForOptions.map(({ label, value, dataqa }) =>
				// show "Container Label" option only when GAPickupCarrier is "Usps"
				(value === EligibleFor.Containerlabel &&
					firstMileCarrier !== Carrier.USPS) ||
				// show "SCAN Form" option only when user's capability is turned on
				(value === EligibleFor.Manifest &&
					!Session.hasCapability(Capability.Scanform)) ||
				// show 'Refund Assist' option only when user's capability is turned on
				(value === EligibleFor.RefundAssist &&
					!Session.hasCapability(Capability.RefundAssistEnabled))
					? null
					: {
							id: parseInt(uniqueId(), 10),
							type: ItemType.Badge,
							title: label,
							active:
								activeMenuItemId === ActiveMenuItemId.EligibleFor &&
								activeMenuItemValue === value,
							showBadge:
								activeMenuItemId === ActiveMenuItemId.EligibleFor &&
								activeMenuItemValue === value &&
								isNumber(totalCount),
							badge: totalCount,
							data: {
								activeMenuItemId: ActiveMenuItemId.EligibleFor,
								activeMenuItemValue: value,
								leftMenuTitle: label,
							},
							dataqa,
						}
			).filter(option => option),
			dataqa: 'eligible-for',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Status',
			active: activeMenuItemId === ActiveMenuItemId.Status,
			collapsed: activeMenuItemId !== ActiveMenuItemId.Status,
			items: StatusOptions.filter(
				option => option.value !== PrintStatus.Any
			).map(({ label, value, dataqa }) => ({
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: label,
				active:
					activeMenuItemId === ActiveMenuItemId.Status &&
					activeMenuItemValue === value,
				showBadge:
					activeMenuItemId === ActiveMenuItemId.Status &&
					activeMenuItemValue === value &&
					isNumber(totalCount),
				badge: totalCount,
				data: {
					activeMenuItemId: ActiveMenuItemId.Status,
					activeMenuItemValue: value,
					leftMenuTitle: label,
				},
				dataqa,
			})),
			dataqa: 'status',
		},
	];

	if (Session.hasCapability(InternalCapability.ManageUsers)) {
		menuItems.push({
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'User',
			active: activeMenuItemId === ActiveMenuItemId.User,
			collapsed: activeMenuItemId !== ActiveMenuItemId.User,
			items: users.map(user => ({
				id: parseInt(uniqueId(), 10),
				type: ItemType.Badge,
				title: user.UserName,
				active:
					activeMenuItemId === ActiveMenuItemId.User &&
					activeMenuItemValue === user.UserId,
				showBadge:
					activeMenuItemId === ActiveMenuItemId.User &&
					activeMenuItemValue === user.UserId &&
					isNumber(totalCount),
				badge: totalCount,
				data: {
					activeMenuItemId: ActiveMenuItemId.User,
					activeMenuItemValue: user.UserId,
					leftMenuTitle: user.UserName,
				},
			})),
			dataqa: 'users',
		});
	}

	const onActionTrigger = (actionType, itemType, data) => {
		if (
			activeMenuItemId === ActiveMenuItemId.Search &&
			actionType === ActionType.Delete
		) {
			// clicking "X" or empty "term" value should switch to filter by "All"
			const defaultActiveMenuItem = getDefaultActiveMenuItem();

			onMenuItemClick(defaultActiveMenuItem);
		} else {
			onMenuItemClick(data);
			localStorage.setItem('activeMenuItemData', JSON.stringify(data));
		}
	};

	useEffect(() => {
		const headerTrigger = Util.getQueryParamByName('eligibleFor') || null;
		const activeMenuItemData = localStorage.getItem('activeMenuItemData');

		// do not apply previous filter if a filter query param exists
		if (activeMenuItemData && !headerTrigger) {
			onMenuItemClick(JSON.parse(activeMenuItemData));
		}
	}, []);

	return (
		<MenuPanel
			items={menuItems}
			placeholder="Search Prints"
			onActionTrigger={onActionTrigger}
			resultCount={totalCount}
			showResult={activeMenuItemId === ActiveMenuItemId.Search}
			onAdvancedSearchTrigger={onAdvancedSearchTrigger}
			showAdvancedSearch
		/>
	);
};

LeftMenu.propTypes = {
	onAdvancedSearchTrigger: PropTypes.func.isRequired,
	onMenuItemClick: PropTypes.func.isRequired,
	users: PropTypes.array.isRequired,
	activeMenuItem: PropTypes.object,
	totalCount: PropTypes.any,
};

export default LeftMenu;
